import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core'
import {
  IReadyForPayment,
  PaymentService,
  ReadyToPayViewODataService,
  IQuickFilter
} from '@exchange/domain/.'
import { BsModalService } from 'ngx-bootstrap/modal'
import {
  ConfirmModalComponent,
  GridSize,
  GridWrapperComponent,
  MultiRowSelection
} from 'common-components'

interface IPaymentModel {
  selected: IReadyForPayment[]
  payAmount: number
}

@Component({
  selector: 'app-ready-to-pay-list',
  templateUrl: './ready-to-pay-list.component.html',
  styleUrls: ['./ready-to-pay-list.component.scss'],
  standalone: false
})
export class ReadyToPayListComponent implements OnInit {
  @ViewChild('grid', { static: true }) grid: GridWrapperComponent
  @Output() generatePayment = new EventEmitter<IReadyForPayment[]>()
  GridSize = GridSize
  defaultFilter = "Status eq 'Pending Payment'"
  model: IPaymentModel = {
    payAmount: 0,
    selected: []
  }

  constructor(
    private paymentService: PaymentService,
    private readyToPayService: ReadyToPayViewODataService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.initializeGrid()
  }

  initializeGrid() {
    this.grid.gridOptions.context = {
      canEdit: false,
      canDelete: false
    }

    this.grid.gridOptions.columnDefs = [
      {
        headerName: 'Adjudication Type',
        field: 'AdjudicationTypeName',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Provider NPI',
        field: 'Npi',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep', defaultOption: 'equals' },
        width: 150,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Provider Name',
        field: 'Name',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'CHI Claim ID',
        field: 'ClaimId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100
      },
      {
        headerName: 'Line #',
        field: 'ServiceLineNumber',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100
      },
      {
        headerName: 'Pay Amount',
        field: 'PayAmount',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: GridWrapperComponent.CurrencyRenderer()
      },
      {
        headerName: 'Market Name',
        field: 'MarketName',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 150,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Patient City',
        field: 'City',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Patient State',
        field: 'State',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Patient Zip Code',
        field: 'ZipCode',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      }
    ]

    this.grid.gridOptions.rowSelection = { ...MultiRowSelection, checkboxes: true }
    this.grid.gridOptions.onSelectionChanged = this.gridSelectionChanged.bind(this)
    this.grid.defaultFilter = this.defaultFilter
    this.grid.odata = this.readyToPayService
  }

  gridSelectionChanged() {
    const rows = this.grid.api.getSelectedRows() as IReadyForPayment[]

    if (rows.length > 0) {
      const selectedProviders = rows
        .map(row => row.Npi)
        .filter((npi, i, npis) => npis.indexOf(npi) === i)
      if (selectedProviders.length > 1) {
        const firstSelectedProvider = this.model.selected[0].Npi
        this.grid.api.forEachNode(node => {
          if (node.data.Npi !== firstSelectedProvider) {
            node.setSelected(false)
          }
        })

        return // deselecting the nodes above will call this method again so just return
      }
    }

    this.model.selected = rows
    this.model.payAmount =
      rows.length < 1 ? 0 : rows.map(row => row.PayAmount).reduce((prev, next) => prev + next)
  }

  filterGrid(quickFilter: IQuickFilter) {
    this.grid.defaultFilter = `${this.defaultFilter} and ${quickFilter.odataFilter}`
    this.refresh()
  }

  resetGridFilter() {
    this.grid.defaultFilter = this.defaultFilter
    this.refresh()
  }

  refresh() {
    this.grid.getData()
  }

  returnToRemitReceived() {
    return this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `Return to Remit Received`,
          message: `Are you sure you want to return all selected service lines to remit received?`
        }
      })
      .content.onClose.subscribe(userConfirmation => {
        if (userConfirmation) {
          this.paymentService
            .returnToInventory(this.model.selected)
            .subscribe(() => this.grid.getData())
        }
      })
  }
}
